import type { ReactNode } from "react";
import { useAuth } from "../../hooks/use-auth";

interface ProtectedRouteProps {
	children: ReactNode;
	allowedRoles?: ("doctor" | "secretary")[];
}

/**
 * Componente para proteger rotas baseado em roles
 * Redireciona para /unauthorized se usuário não tiver permissão
 *
 * @example
 * // Rota acessível apenas para DOCTOR
 * <ProtectedRoute allowedRoles={["doctor"]}>
 *   <MedicalRecordsPage />
 * </ProtectedRoute>
 *
 * @example
 * // Rota acessível para ambos os roles
 * <ProtectedRoute>
 *   <DashboardPage />
 * </ProtectedRoute>
 */
export default function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
	const { user } = useAuth();

	// Se não há restrição de roles, permitir acesso
	if (!allowedRoles || allowedRoles.length === 0) {
		return <>{children}</>;
	}

	// Se usuário não estiver autenticado, redirecionar para login
	// (Isso é redundante com app.tsx, mas é uma camada extra de segurança)
	if (!user) {
		return <Navigate to="/login" replace />;
	}

	// Se usuário não tiver role permitido, redirecionar para página de acesso negado
	if (!allowedRoles.includes(user.role)) {
		return <Navigate to="/unauthorized" replace />;
	}

	// Usuário tem permissão, renderizar conteúdo
	return <>{children}</>;
}
